import { Palette } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { menuRowClass } from "@/components/ui/menu-surface";
import {
  PALETTES,
  THEME_CHOICES,
  isTheme,
  type Theme,
} from "@/engine/support/palette";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/engine/support/classNames";
import { SettingsSelectRow } from "./SettingsGroup";

/** What System is drawn as: the two it chooses between. */
const SYSTEM_SWATCH = [PALETTES[0].swatch[0], PALETTES[1].swatch[0]];

/**
 * A palette's colours as dots, so it is seen before it is chosen. The values
 * are the palette's own tokens, held in step by `palette.test.ts`.
 */
export function PaletteSwatch({ theme }: { theme: Theme }) {
  const colours =
    theme === "system"
      ? SYSTEM_SWATCH
      : (PALETTES.find(({ id }) => id === theme)?.swatch ?? []);
  return (
    <span aria-hidden="true" className="flex shrink-0">
      {colours.map((colour, i) => (
        <span
          key={i}
          className="-ml-1 h-3.5 w-3.5 rounded-full border border-foreground/20 first:ml-0"
          style={{ backgroundColor: colour }}
        />
      ))}
    </span>
  );
}

/**
 * The theme control, on the shared menu surface beside the language row: a
 * select for the same reason that one is, since a row per palette would make
 * the menu longer than the rest of it.
 */
export function ThemeMenuRow({
  className,
  variant = "menu",
}: {
  className?: string;
  /**
   * `menu`: a row of the dropdown, with the select at its end. `row`: the
   * whole row opens the list, for a settings card in a narrow column.
   * `compact`: just the select, swatch inside, for a header.
   */
  variant?: "menu" | "row" | "compact";
}) {
  const { t } = useTranslation();
  const { theme, resolvedTheme, setTheme } = useTheme();
  // System names what it resolves to, so the choice reads as the result.
  const label = (choice: Theme) =>
    choice === "system"
      ? `${t("settings.palette.system")} (${t(`settings.palette.${resolvedTheme}`)})`
      : t(`settings.palette.${choice}`);

  const items = THEME_CHOICES.map((choice) => (
    <SelectItem
      key={choice}
      value={choice}
      data-testid={`theme-option-${choice}`}
    >
      <span className="flex items-center gap-2">
        <PaletteSwatch theme={choice} />
        {label(choice)}
      </span>
    </SelectItem>
  ));
  const onValueChange = (next: string) => {
    if (isTheme(next)) setTheme(next);
  };

  if (variant === "row") {
    return (
      <SettingsSelectRow
        menuAction
        data-testid="theme-select"
        aria-label={t("settings.chooseTheme")}
        icon={<Palette />}
        label={t("settings.theme")}
        chosenMark={<PaletteSwatch theme={theme} />}
        chosen={label(theme)}
        value={theme}
        onValueChange={onValueChange}
      >
        {items}
      </SettingsSelectRow>
    );
  }

  if (variant === "compact") {
    return (
      <Select value={theme} onValueChange={onValueChange}>
        <SelectTrigger
          data-testid="theme-select"
          aria-label={t("settings.chooseTheme")}
          className={cn("h-9 w-auto gap-2 text-sm", className)}
        >
          <div className="flex items-center gap-2 whitespace-nowrap">
            <PaletteSwatch theme={theme} />
            {label(theme)}
          </div>
        </SelectTrigger>
        <SelectContent onKeyDown={(event) => event.stopPropagation()}>
          {items}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className={cn(menuRowClass, "cursor-default", className)}>
      <span className="flex h-5 w-5 sm:h-6 sm:w-6 shrink-0 items-center justify-center text-muted-foreground">
        <Palette className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 text-left">{t("settings.theme")}</span>
      <Select value={theme} onValueChange={onValueChange}>
        <SelectTrigger
          data-menu-action
          data-testid="theme-select"
          aria-label={t("settings.chooseTheme")}
          className="h-8 w-auto min-w-28 gap-2 text-xs"
        >
          {/* A div: the trigger clamps a span child to one line by making it a
              box, which stacks the swatch over the name. */}
          <div className="flex items-center gap-2 whitespace-nowrap">
            <PaletteSwatch theme={theme} />
            {label(theme)}
          </div>
        </SelectTrigger>
        {/* The list is portalled, but React bubbles its keys through the
            portal to the panel around this row, which closes on Escape: an
            Escape meant for the list would close the settings with it. */}
        <SelectContent onKeyDown={(event) => event.stopPropagation()}>
          {items}
        </SelectContent>
      </Select>
    </div>
  );
}
